import React, { useEffect, useRef} from "react";
import {Draggable} from "react-beautiful-dnd";

import {
    CHOOSE_IMAGE_TEXT,
    DELETE_OPT_BTN, GRADE_SCHEMA, imageSuffixArr,
    MC_ANSWER_LENGTH, OPTION_LABEL,
    TOGGLE_MENU_BAR_TEXT,
} from "../../utils/constants";
import {notifyError, send2ScreenReader, stripHTMLTags} from "../../utils/helpers";
import {updateAnswers, updateCorrectAnswers} from "../../utils/questionUpdate";
import {INVALID_IMAGE_FORMAT_ERROR, SELECT_FILE_ERROR} from "../../utils/toast-message-constants";
import "../styles.css";
import {EditorContent, useEditor} from "@tiptap/react";
import Underline from "@tiptap/extension-underline";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import CharacterCount from "@tiptap/extension-character-count";
import tiptapClasses from "../../components/Tiptap/Tiptap.module.css";
import {ReactComponent as DRAG} from "../../images/drag_flat_grey.svg";
import {ReactComponent as TOGGLE} from "../../images/font_gray.svg";
import {ReactComponent as DELETE} from "../../images/trash_red.svg";
import {ReactComponent as ATTACHMENT} from "../../images/attachment_grey.svg";
import { ReactComponent as CORRECT_ICON } from "../../images/grade-question-correct-icon.svg";
import {Color} from "@tiptap/extension-color";
import TextStyle from '@tiptap/extension-text-style';
import Highlight from "@tiptap/extension-highlight";
import classes from "../questions.module.css";
import {TipTapMenuBarNew} from "../../components/Tiptap/TipTapMenuBarNew";
import {useImageLoaderManager} from "../../hooks/useImageLoaderManager";
import {PollApiCalls} from "../../apis/PollApiCalls";
import Skeleton from "react-loading-skeleton";
import OptionImageViewer from "../OptionImageViewer";

export default function MCQuestionOptionNew({
                                                gradeSchema,
                                                getPollKey,
                                                optionData,
                                                selectedQuestion,
                                                questions,
                                                setQuestions,
                                                questionBankId,
                                                index,
                                                maxSize,
                                                warningShowed,
                                                setWarningShowed,
                                                questionBuilder = false,
                                                disabled = false,
                                                collapsed,
                                                setCollapsed,
                                                deleteCollapsed,
}) {
    const {
        createUniqueImage,
        resetImage,
        checkImageIsLoading,
        setOptionImageLoading,
        setImage
    } = useImageLoaderManager(questions, setQuestions);
    const {uploadOptImage, uploadQueBankImage} = PollApiCalls();
    const {serialNo, optionsMap, correctAnswers=[], weightage, optionsImageMap = {}} = selectedQuestion;
    const gradeEnable = gradeSchema !== GRADE_SCHEMA.UNGRADED.key && !!weightage;
    const {choice, optionValue} = optionData;
    const selectedQuestionIndex = serialNo - 1;
    const uploadInputRef = useRef();


    let editor = useEditor({
        shouldRerenderOnTransaction: true,
        editorProps: {
            attributes: {
                'aria-label': `Option ${choice.toUpperCase()} Rich Text Editor`,
                'aria-multiline': 'true',
                'aria-readonly': 'false',
            },
            transformPastedHTML(text) {
                let cleanText = stripHTMLTags(text);
                let replace = cleanText.replace(/[\n\r]+/g, '');
                return replace;
            },
            transformPastedText(text) {
                let cleanText = stripHTMLTags(text);
                let replace = cleanText.replace(/[\n\r]+/g, '');
                return replace;
            },
        },
        extensions: [
            Underline,
            StarterKit,
            Placeholder.configure({
                placeholder: OPTION_LABEL + " " + choice.toUpperCase(),
            }),
            CharacterCount.configure({limit: MC_ANSWER_LENGTH}),
            TextStyle,
            Color,
            Highlight,
            // Mathematics.configure({
            //     regex: LATEX_REG_EXP,
            // }),
        ],
        onUpdate: ({editor}) => {
            handleAnswerChange(editor.getHTML(), choice);
        },
        content: optionValue,
    }, [serialNo]);

    useEffect(() => {
        if (optionValue !== editor.getHTML()) {
            editor.commands.setContent(optionValue);
        }

    }, [optionValue, editor]);


    const handleAnswerChange = (input, itemKey) => {
        setQuestions(
            updateAnswers(input, itemKey, selectedQuestionIndex, questions)
        );
    };
    const handleCorrectAnswerChange = (input) => {
        setQuestions(updateCorrectAnswers(input, selectedQuestionIndex, questions));
    };

    const handleDeleteOption = () => {
        let newQuestions = [];

        questions.forEach((q) => {
            if (q.serialNo === serialNo) {
                let updatedQuestion = {...q};
                let newOptionsMap = {};
                Object.entries(q.optionsMap).forEach(([key, value]) => {
                    if (key !== choice) {
                        if (key > choice) {
                            const newKey = String.fromCharCode(key.charCodeAt(0) - 1);
                            newOptionsMap[newKey] = value;
                        } else {
                            newOptionsMap[key] = value;
                        }
                    }
                });

                updatedQuestion.optionsMap = newOptionsMap;
                updatedQuestion.correctAnswers = q.correctAnswers.includes(choice) ? q.correctAnswers.filter(answer => answer !== choice) : q.correctAnswers;


                if (!!q.optionsImageMap) {
                    let newOptionsImageMap = {};
                    Object.entries(q.optionsImageMap).forEach(([key, value]) => {
                        if (key !== choice) {
                            if (key > choice) {
                                const newKey = String.fromCharCode(key.charCodeAt(0) - 1);
                                newOptionsImageMap[newKey] = value;
                            } else {
                                newOptionsImageMap[key] = value;
                            }
                        }
                    });
                    updatedQuestion.optionsImageMap = newOptionsImageMap;
                }

                // updatedQuestion["image" + choice] = null;
                newQuestions.push(updatedQuestion);
            } else {
                newQuestions.push({...q});
            }
        });
        deleteCollapsed();
        setQuestions(newQuestions);
        send2ScreenReader(`Option is deleted.`);
    };


    const uploadPollImg = async (file) => {
        const suffix = file.name.substring(file.name.lastIndexOf('.'))?.toLowerCase();
        if (!imageSuffixArr.includes(suffix)) {
            notifyError(INVALID_IMAGE_FORMAT_ERROR);
            uploadInputRef.current.value = '';
            return;
        }
        if (!file || !file.name) { // if no image is selected
            notifyError(SELECT_FILE_ERROR);
            return;
        }
        const fileName = createUniqueImage(file.name);
        const f = new File([file], fileName);
        let uuid = getPollKey();
        uploadOptImage(uuid, f, (name, url) => {
            setImage(name, url);
        }).then(res => {
            if (!res) {
                resetImage(fileName);
            }
        });


        setOptionImageLoading(serialNo, choice, fileName);
    }


    const uploadQueBankImg = (file) => {
        const suffix = file.name.substring(file.name.lastIndexOf('.'))?.toLowerCase();
        if (!imageSuffixArr.includes(suffix)) {
            notifyError(INVALID_IMAGE_FORMAT_ERROR);
            uploadInputRef.current.value = '';
            return;
        }
        if (!file || !file.name) { // if no image is selected
            notifyError(SELECT_FILE_ERROR);
            return;
        }
        const fileName = createUniqueImage(file.name);
        const f = new File([file], fileName);
        uploadQueBankImage(questionBankId, f, (name, url) => {
            setImage(name, url);
        }).then(res => {
            if (!res) {
                resetImage(fileName);
            }
        });

        setOptionImageLoading(serialNo, choice, fileName);
    }


    const handleUpload = (e) => {
        if (!questionBankId) {
            uploadPollImg(e.target.files[0]);
        } else {
            uploadQueBankImg(e.target.files[0]);
        }
    };

    const handleDelete = () => {
        const questionsCopy = JSON.parse(JSON.stringify(questions));
        const questionCopy = questionsCopy.find(q => q.serialNo === serialNo);
        if (!questionCopy) {
            return;
        }
        const {optionsImageMap={}} = questionCopy;
        if (optionsImageMap.hasOwnProperty(choice)) {
            delete optionsImageMap[choice];
            setQuestions(questionsCopy);
        }
    }

    const renderIndexAndImage = (dragHandleProps) => {
        const hasOptionImage = optionsImageMap.hasOwnProperty(choice) && !!optionsImageMap[choice].imageURL;
        let isUploading = false;
        if (hasOptionImage) {
            if (!optionsImageMap.hasOwnProperty(choice)) {
                isUploading = false;
            } else {
                isUploading = checkImageIsLoading(optionsImageMap[choice]);
            }

        }

        return (
            <>
                <span {...dragHandleProps} style={{marginLeft: 12}}>
                    <DRAG/>
                </span>
                <span
                    className={`${classes.optionIndex} ${gradeEnable && correctAnswers.includes(choice) ? classes.correct : ""}`}
                >
                    {choice.toUpperCase()}
                </span>
                {hasOptionImage && !isUploading &&
                    <OptionImageViewer
                        imageURL={optionsImageMap[choice].imageURL}
                        choice={choice}
                        handleDelete={handleDelete}
                    />
                }

                {hasOptionImage && !!isUploading &&
                    <div className={classes.optionImageViewerWrapper} style={{display: "unset"}}>
                        <Skeleton highlightColor="lightgrey" width="100%" height="100%" borderRadius={6}/>
                    </div>
                }
            </>
        );
    }


    const renderOptionManageBar = () => {
        return (
            <div style={{display: "flex", gap: 5, alignItems: "center", paddingRight: 12}}>
                {(!optionsImageMap.hasOwnProperty(choice) || !optionsImageMap[choice].imageURL) &&
                    <>
                        <span
                            title={CHOOSE_IMAGE_TEXT}
                            tabIndex={0}
                            role={"button"}
                            aria-label={CHOOSE_IMAGE_TEXT}
                            style={{cursor: "pointer",}}
                            onClick={(e) => uploadInputRef.current.click(e)}
                        >
                            <ATTACHMENT/>
                            <input
                                ref={uploadInputRef}
                                style={{display: "none"}}
                                type="file"
                                accept="image/png, image/jpg, image/jpeg, image/jfif, image/pjpeg, image/pjp"
                                onChange={handleUpload}
                            />
                        </span>
                        <span className={classes.iconDivider}/>
                    </>
                }


                <span
                    tabIndex={0}
                    style={{cursor: "pointer",}}
                    title={TOGGLE_MENU_BAR_TEXT}
                    role={"button"}
                    aria-label={TOGGLE_MENU_BAR_TEXT}
                    onClick={() => setCollapsed(!collapsed)}
                >
                    <TOGGLE/>
                </span>

                {Object.keys(optionsMap).length > 2 &&
                    <>
                        <span className={classes.iconDivider}/>
                        <span
                            tabIndex={0}
                            style={{cursor: "pointer",}}
                            title={DELETE_OPT_BTN.slice(0, -1)}
                            role={"button"}
                            aria-label={DELETE_OPT_BTN.slice(0, -1)}
                            onClick={handleDeleteOption}
                        >
                           <DELETE/>
                        </span>
                    </>
                }
            </div>
        );
    }

    return (

        <Draggable
            draggableId={"option_draggable_" + index}
            index={index}
        >

            {(provided, snapshot) => (
                <div
                    className={classes.optionBarWrapper}
                    ref={provided.innerRef}
                    {...provided.draggableProps}

                    tabIndex={-1}
                >
                    {gradeEnable &&
                        <div className={classes.optionCheckBoxContainer}>
                            <span
                                tabIndex={0}
                                role="checkbox"
                                aria-checked={correctAnswers.includes(choice) ? "true" : "false"}
                                aria-label={`Check to toggle Option ${choice.toUpperCase()} as the correct answer`}
                                onClick={() => handleCorrectAnswerChange(choice)}
                                className={`${classes.optionCheckBox} ${correctAnswers.includes(choice) ? classes.active : ""}`}
                                onKeyDown={(event) => {
                                    if (event.key === ' ') {
                                        event.preventDefault();
                                        handleCorrectAnswerChange(choice);
                                    }
                                }}
                            >
                                {correctAnswers.includes(choice) &&
                                    <CORRECT_ICON/>
                                }
                            </span>
                        </div>
                    }

                    <div className={classes.optionBar}>
                        {!!collapsed && renderIndexAndImage(provided.dragHandleProps)}

                        <span className={classes.optionEditorWrapper}>
                            <div className={`${classes.optionEditorMenuBar} ${collapsed ? classes.collapsed : ""}`}>
                                <TipTapMenuBarNew
                                    isOpen={!collapsed}
                                    editor={editor}
                                    selectedQuestion={selectedQuestion}
                                />
                                {!collapsed && renderOptionManageBar()}
                            </div>

                            <div style={{display: "flex", gap: 10, alignItems: "center"}}>
                                {!collapsed && renderIndexAndImage(provided.dragHandleProps)}
                                <EditorContent
                                    editor={editor}
                                    style={{cursor: "text", flex: 1, minWidth: 0, height: "100%", paddingRight: collapsed ? 10 : undefined}}
                                    className={`${tiptapClasses.editorContentBox} ${collapsed ? tiptapClasses.noneOutLine : ""}`}
                                />
                            </div>


                            {(!collapsed || (!!editor.isFocused && editor.storage.characterCount.characters() > 0)) &&
                                <span className={`${classes.characterCountBox} ${collapsed ? classes.inline : ""}`}>
                                    {collapsed
                                        ? editor.storage.characterCount.characters()
                                        : `${editor.storage.characterCount.characters()}/${MC_ANSWER_LENGTH}`
                                    }
                                </span>
                            }


                        </span>

                        {!!collapsed && renderOptionManageBar()}
                    </div>
                </div>
            )}
        </Draggable>
    );
}
